import {
  Integration,
  MerchantCashAdvance,
  MerchantCashAdvanceDocument,
  MerchantCashAdvanceStatusHistoryDocument,
  Rights,
  Transaction,
  TransactionStatus,
  useCancelTransactionMutation,
  useRefreshMerchantCashAdvanceTransactionsMutation,
  useStopTransactionMutation,
  useSaveTransactionTypeMutation,
  useMcaFeeTransactionsQuery,
  Recurrency
} from '../../graphql/types';
import {
  BarsOutlined,
  CopyOutlined,
  EditOutlined,
  InteractionOutlined,
  LinkOutlined
} from '@ant-design/icons';
import { Backdrop, Box, Button, CircularProgress, Dialog, IconButton, LinearProgress, Stack, Tooltip } from '@mui/material';
import 'ag-grid-enterprise/styles/ag-grid.css';
import 'ag-grid-enterprise/styles/ag-theme-material.css';
import 'assets/third-party/ag-theme-material-dark.scss';
import useConfig from '../../hooks/useConfig';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState, forwardRef, useImperativeHandle } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { DefaultAgGridTooltip } from '../../components/ag-grid/DefaultAgGridTooltip';
import MainCard from '../../components/MainCard';
import { useTheme } from '@mui/material/styles';
import { EditTwoTone, PlusOutlined, ReloadOutlined, StopOutlined } from '@ant-design/icons';
import {
  ColumnMovedEvent,
  ColumnResizedEvent,
  ColumnVisibleEvent,
  SortChangedEvent,
  GetContextMenuItemsParams,
  GridReadyEvent,
  MenuItemDef,
  ColDef,
  FilterChangedEvent
} from 'ag-grid-community';
import { GridApi, ICellRendererParams, SideBarDef } from 'ag-grid-enterprise';
import { getFormattedDateUTC } from '../../utils/getFormattedDate';
import { dispatch } from '../../store';
import { openSnackbar } from '../../store/reducers/snackbar';
import { getErrorMessage, requireMutationData } from '../../utils/graphqlMutationResult';
import ActionConfirmation from '../../components/mca/ActionConfirmation';
import useAuth from '../../hooks/useAuth';
import EditTransactionNote from './EditTransactionNote';
import PlatformCompanyLogo from '../../utils/platformCompanyLogo';
import accountCellRenderer from '../../components/ag-grid/AgGridAccountCellRenderer';
import {
  TransactionAmountCell,
  TransactionDefaultTypeCell,
  TransactionStatusCell
} from '../../components/ag-grid/AgGridTransactionsChipCell';
import { reportApi } from '../../api/reportApi';
import { allowedEditableTransactionTypes } from '../../pages/transactions/transactions-list';
import McaTransactionsContextMenu from './MCATransactionsContextMenu';
import { clearedTransactions, manualTransactions, sharedCompactViewKey } from './McaTransactions';
import AddRecurrence from './AddRecurrence';
import RecurrencyAchDetailsDialog from './RecurrencyAchDetailsDialog';

import AddManualTransaction from './AddManualTransaction';

import { Icon, supportedTransactionTypesforIcons, supportedPaymentMethodIdsforIcons } from '../../components/icons';
import TransactionsTypeLogo from '../../utils/transactionsTypeLogo';
import PaymentMethodsLogo from '../../utils/paymentMethodLogo';
import { underscoreToSpace } from '../../utils/strings';
import useAgGridOutsideClick from '../../components/ag-grid/useAgGridOutsideClick';
import { applyGridColumnState } from '../../utils/agGridUtils';
import CustomTooltip from 'components/CustomTooltip';

interface Props {
  mca: MerchantCashAdvance;
}

interface McaFeesTransactionsForwardRef {
  reloadTransactionsFromProcessor: () => void;
}
export const mcaFeeTransactionKeyName = 'MCAS_FEES_GRID';
const McaFeesTransactions = forwardRef<McaFeesTransactionsForwardRef, Props>(({ mca }, ref) => {
  const theme = useTheme();
  const { mode } = useConfig();
  const { user: currentUser } = useAuth();
  const userId = currentUser?.id;
  const keyName = `${userId}_${mcaFeeTransactionKeyName}`;
  const compactViewKey = `${userId}_${sharedCompactViewKey}`;
  const useCompactView = localStorage.getItem(compactViewKey + '_USE_COMPACT_VIEW') === 'false' ? null : true;
  const isSuperAdmin = currentUser?.rights.includes(Rights.IsSuperAdmin);
  const canManage = currentUser?.rights.includes(Rights.ManageMcaTransactions) && mca.currentUserRights.includes(Rights.EditMca);
  const gridStyle = useMemo(() => ({ height: '100%', width: '100%' }), []);
  const { loading, data, refetch } = useMcaFeeTransactionsQuery({
    variables: { id: mca.id },
    fetchPolicy: 'network-only'
  });
  const [updateTransactionType] = useSaveTransactionTypeMutation();
  const transactions = useMemo(() => (data?.mcaFeeTransactions || []) as Transaction[], [data]);
  const [cancelTransaction] = useCancelTransactionMutation();
  const [stopTransaction] = useStopTransactionMutation();
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [transaction, setTransaction] = useState<Transaction | null>(null);
  const [manualEdit, setManualEdit] = useState<boolean>(false);
  const handleManualEdit = () => {
    setManualEdit(!manualEdit);
    if (transaction && !manualEdit) setTransaction(null);
  };
  const [editNote, setEditNote] = useState<boolean>(false);
  const handleEditNote = () => {
    setEditNote(!editNote);
    if (transaction && !editNote) setTransaction(null);
  };
  const [refreshMerchantCashAdvanceTransactionsMutation] = useRefreshMerchantCashAdvanceTransactionsMutation();
  const [backdropOpen, setBackdropOpen] = useState<boolean>(false);
  const [openCancelConfirmation, setOpenCancelConfirmation] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState<boolean>(false);
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [openRecurrencyAchDetails, setOpenRecurrencyAchDetails] = useState<boolean>(false);
  const [selectedRecurrency, setSelectedRecurrency] = useState<Recurrency | null>(null);

  const handleCancelTransaction = async (approved: boolean) => {
    setOpenCancelConfirmation(false);
    if (!approved || !transaction) {
      return;
    }
    setBackdropOpen(true);
    try {
      if (manualTransactions.includes(transaction.type)) {
        const result = await cancelTransaction({
          variables: { id: transaction.id },
          refetchQueries: [
            { query: MerchantCashAdvanceDocument, variables: { id: mca?.id } },
            { query: MerchantCashAdvanceStatusHistoryDocument, variables: { id: mca?.id } }
          ]
        });
        requireMutationData(result, (data) => data?.cancelTransaction, 'Transaction was not canceled.');
      } else {
        const result = await stopTransaction({
          variables: {
            transactionId: transaction.id
          },
          refetchQueries: [
            { query: MerchantCashAdvanceDocument, variables: { id: mca?.id } },
            { query: MerchantCashAdvanceStatusHistoryDocument, variables: { id: mca?.id } }
          ]
        });
        requireMutationData(result, (data) => data?.stopTransaction, 'Transaction was not canceled.');
      }
      dispatch(
        openSnackbar({
          open: true,
          message: 'Transaction canceled successfully.',
          variant: 'alert',
          alert: {
            color: 'success'
          },
          close: true
        })
      );
      refetch();
    } catch (error) {
      dispatch(
        openSnackbar({
          open: true,
          message: getErrorMessage(error, 'Failed to cancel transaction.'),
          variant: 'alert',
          alert: { color: 'error' },
          close: true
        })
      );
    } finally {
      setBackdropOpen(false);
    }
  };

  const handleRefetch = async () => {
    setBackdropOpen(true);
    try {
      const result = await refreshMerchantCashAdvanceTransactionsMutation({
        variables: {
          mcaId: mca.id
        }
      });
      requireMutationData(result, (data) => data?.refreshMerchantCashAdvanceTransactions, 'Transactions were not reloaded.');
      dispatch(
        openSnackbar({
          open: true,
          message: 'Transactions have been reloaded from processors.',
          variant: 'alert',
          alert: {
            color: 'success'
          },
          close: true
        })
      );
      refetch();
    } catch (error) {
      dispatch(
        openSnackbar({
          open: true,
          message: `Failed to reload transactions: ${getErrorMessage(error, 'Operation failed')}`,
          variant: 'alert',
          alert: {
            color: 'error'
          },
          close: true
        })
      );
    } finally {
      setBackdropOpen(false);
    }
  };

  useImperativeHandle(ref, () => ({
    reloadTransactionsFromProcessor: () => handleRefetch()
  }));

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      editable: false,
      sortable: true,
      resizable: true,
      pivot: true,
      filter: false,
      cellStyle: {
        display: 'flex',
        alignItems: 'center'
      },
      suppressHeaderMenuButton: true,
      columnChooserParams: {
        suppressColumnFilter: true,
        contractColumnSelection: true
      },
      tooltipComponent: DefaultAgGridTooltip
    };
  }, []);

  const sideBar = useMemo<SideBarDef>(() => {
    return {
      position: 'right',
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressColumnFilter: true,
            suppressColumnSelectAll: true,
            suppressColumnExpandAll: true
          }
        },
        {
          id: 'filters',
          labelDefault: 'Filters',
          labelKey: 'filters',
          iconKey: 'filter',
          toolPanel: 'agFiltersToolPanel',
          toolPanelParams: {
            suppressFilterSearch: true
          }
        }
      ]
    };
  }, []);

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        suppressHeaderMenuButton: true,
        headerName: '#',
        field: 'number',
        cellClass: 'ag-centered-cell',
        headerClass: 'ag-header-transaction-cell-center',
        toolPanelClass: ['ag-header-transaction-cell-center']
      },

      {
        suppressColumnsToolPanel: !currentUser?.rights.includes(Rights.ViewMcaIntegration),
        initialHide: !currentUser?.rights.includes(Rights.ViewMcaIntegration),
        suppressHeaderMenuButton: true,
        headerName: 'Platform',
        headerComponent: () => {
          return (
            <CustomTooltip
              title="Platform"
            >
              <InteractionOutlined style={{ fontSize: 20 }} />
            </CustomTooltip>
          );
        },
        field: 'integration.name',
        tooltipField: 'integration.name',
        cellClass: 'ag-centered-cell',
        headerClass: 'ag-header-transaction-cell-center',
        toolPanelClass: ['ag-header-transaction-cell-center'],
        cellRenderer: ({ data, node }: ICellRendererParams) => {
          if (node.isRowPinned()) {
            return null;
          }
          if (data.integration == null) {
            if (supportedTransactionTypesforIcons.includes(data?.type)) {
              return (
                <Tooltip content={data?.type}>
                  <span className="ag-centered-cell">
                    <Icon id={data?.paymentMethod?.id} size="xxs" />
                  </span>
                </Tooltip>
              );
            } else {
              return <TransactionsTypeLogo type={data?.type} size={'xs'} mt={0} />;
            }
          } else {
            return <PlatformCompanyLogo integration={data?.integration as Integration} size={'xs'} mt={0} />;
          }
        }
      },
      {
        headerName: 'Name',
        field: 'name',
        tooltipField: 'name',
        headerClass: 'ag-header-cell-merchant-group',
        toolPanelClass: ['ag-header-cell-merchant-group']
      },
      {
        initialHide: true,
        headerName: 'Origination Date',
        headerClass: 'ag-header-performance-group-right',
        toolPanelClass: ['ag-header-performance-group-right'],
        field: 'originationDate',
        type: 'rightAligned',
        valueGetter: (params) => getFormattedDateUTC(params.data?.originationDate)
      },
      {
        headerName: 'Effective Date',
        headerClass: 'ag-header-performance-group-right',
        toolPanelClass: ['ag-header-performance-group-right'],
        suppressHeaderMenuButton: true,
        sort: 'desc',
        field: 'effectiveDate',
        type: 'rightAligned',
        valueGetter: (params) => getFormattedDateUTC(params.data?.effectiveDate),
        comparator: (valueA, valueB, nodeA, nodeB, isDescending) => {
          let dateA = new Date(valueA).getTime();
          let dateB = new Date(valueB).getTime();
          if (dateA === dateB) return 0;
          return dateA > dateB ? 1 : -1;
        }
      },
      {
        headerName: 'Type',
        field: 'type',
        headerClass: 'ag-header-performance-group',
        toolPanelClass: ['ag-header-performance-group'],
        filter: true,
        cellRenderer: TransactionDefaultTypeCell,
        cellRendererParams: { useCompactViewStyle: useCompactView ? true : false }
      },
      {
        headerName: 'Pmt Method',
        field: 'paymentMethod.id',
        cellClass: 'ag-centered-cell',
        headerClass: 'text-center-lightskyblue',
        toolPanelClass: ['ag-header-performance-group'],
        sortable: false,
        tooltipValueGetter: (params) => {
          const id = params.data?.paymentMethod?.id;
          return id ? underscoreToSpace(id) : 'UNKNOWN';
        },
        cellRenderer: ({ data, node }: ICellRendererParams) => {
          if (node.isRowPinned()) {
            return null;
          }
          if (supportedPaymentMethodIdsforIcons.includes(data?.paymentMethod?.id)) {
            return (
              <Tooltip content={data?.paymentMethod?.id}>
                <span className="ag-centered-cell">
                  <Icon id={data?.paymentMethod?.id} size="xxs" />
                </span>
              </Tooltip>
            );
          } else {
            return <PaymentMethodsLogo paymentMethod={data?.paymentMethod} size={'xs'} mt={0} />;
          }
        }
      },
      {
        headerName: 'Account',
        headerClass: 'ag-header-performance-group',
        toolPanelClass: ['ag-header-performance-group'],
        field: 'account',
        tooltipValueGetter: (params) => accountCellRenderer(params),
        cellClass: 'textFormat',
        cellRenderer: accountCellRenderer
      },
      {
        headerName: 'Status',
        headerClass: 'ag-header-performance-group',
        toolPanelClass: ['ag-header-performance-group'],
        field: 'status',
        filter: true,
        cellRendererParams: { useCompactViewStyle: useCompactView ? true : false },
        cellRenderer: TransactionStatusCell
      },
      {
        headerName: 'Amount',
        headerClass: 'ag-header-performance-group-right',
        toolPanelClass: ['ag-header-performance-group-right'],
        field: 'amount',
        cellRenderer: TransactionAmountCell,
        cellRendererParams: { useCompactViewStyle: useCompactView ? true : false },
        type: 'rightAligned'
      },
      {
        headerName: 'Deposit Date',
        field: 'depositDate',
        headerClass: 'ag-header-performance-group-right',
        toolPanelClass: ['ag-header-performance-group-right'],
        type: 'rightAligned',
        valueGetter: (params) => getFormattedDateUTC(params.data?.depositDate)
      },
      {
        initialHide: true,
        headerName: 'Notes',
        field: 'notes',
        headerClass: 'ag-header-performance-group',
        toolPanelClass: ['ag-header-performance-group'],
        tooltipField: 'notes'
      },
      {
        initialHide: true,
        headerName: 'To',
        field: 'to',
        headerClass: 'ag-header-performance-group',
        toolPanelClass: ['ag-header-performance-group'],
        tooltipField: 'to'
      },
      {
        initialHide: true,
        headerName: 'Return Date',
        headerClass: 'ag-header-fees-group-right',
        toolPanelClass: ['ag-header-fees-group-right'],
        field: 'returnDate',
        type: 'rightAligned',
        valueGetter: (params) => getFormattedDateUTC(params.data?.returnDate)
      },
      {
        headerName: 'Return Code',
        headerClass: 'lightsalmon',
        field: 'returnCode',
        toolPanelClass: ['ag-header-fees-group-right']
      },
      {
        initialHide: true,
        headerName: 'Recurrence Description',
        field: 'recurrenceDescription',
        headerClass: 'ag-header-transaction-cell',
        toolPanelClass: ['ag-header-transaction-cell'],
        tooltipField: 'recurrenceDescription'
      },
      {
        initialHide: true,
        headerName: 'Transaction ID',
        headerComponent: () => {
          return (
            <CustomTooltip
              title="Transaction ID"
            >
              <LinkOutlined style={{ fontSize: 20 }} />
            </CustomTooltip>
          );
        },
        headerClass: 'ag-header-transaction-cell-center',
        toolPanelClass: ['ag-header-transaction-cell-center'],
        field: 'externalId',
        tooltipField: 'externalId'
      },
      {
        field: 'actions',
        headerName: '',
        hide: !canManage,
        initialHide: !canManage,
        headerComponent: () => {
          return (
            <CustomTooltip
              title="Actions"
            >
              <BarsOutlined style={{ fontSize: 20 }} />
            </CustomTooltip>
          );
        },
        cellRenderer: ({ data, node }: ICellRendererParams) => {
          if (node.isRowPinned()) {
            return null;
          }
          switch (true) {
            case data.status === TransactionStatus.Processed && data.integration !== undefined:
              return (
                <>
                  <span className="hover-action-content">
                    <CustomTooltip title="Edit Note">
                      <IconButton
                        sx={{ color: theme.palette.lightgreen.main }}
                        onClick={(e: any) => {
                          e.stopPropagation();
                          setTransaction(data);
                          handleEditNote();
                        }}
                      >
                        <EditOutlined style={{ color: 'lightgreen' }} />
                      </IconButton>
                    </CustomTooltip>
                  </span>
                  <span className="hover-action-content">
                    <CustomTooltip title="Cancel">
                      <IconButton
                        sx={{
                          color: theme.palette.warning.main
                        }}
                        onClick={(e: any) => {
                          e.stopPropagation();
                          setTransaction(data);
                          setOpenCancelConfirmation(true);
                        }}
                      >
                        <StopOutlined twoToneColor={theme.palette.warning.main} />
                      </IconButton>
                    </CustomTooltip>
                  </span>
                  <span className="hover-action-content">
                    <CustomTooltip title="Duplicate">
                      <IconButton
                        sx={{ color: theme.palette.slateblue.main }}
                        onClick={(e: any) => {
                          e.stopPropagation();
                          setTransaction(data);
                          setOpenCreateDialog(true);
                          setOpenEditDialog(true);
                        }}
                      >
                        <CopyOutlined style={{ color: 'slateblue' }} />
                      </IconButton>
                    </CustomTooltip>
                  </span>
                </>
              );
            case !manualTransactions.includes(data.type) && data.integration !== undefined:
              return (
                <>
                  <span className="hover-action-content">
                    <CustomTooltip title="Edit Note">
                      <IconButton
                        sx={{ color: theme.palette.lightgreen.main }}
                        onClick={(e: any) => {
                          e.stopPropagation();
                          setTransaction(data);
                          handleEditNote();
                        }}
                      >
                        <EditOutlined style={{ color: 'lightgreen' }} />
                      </IconButton>
                    </CustomTooltip>
                  </span>
                  <span className="hover-action-content">
                    <CustomTooltip title="Duplicate">
                      <IconButton
                        sx={{ color: theme.palette.skyblue.main }}
                        onClick={(e: any) => {
                          e.stopPropagation();
                          setTransaction(data);
                          setOpenEditDialog(true);
                        }}
                      >
                        <CopyOutlined style={{ color: 'skyblue' }} />
                      </IconButton>
                    </CustomTooltip>
                  </span>
                </>
              );
            case manualTransactions.includes(data.type) && data.status !== TransactionStatus.Cancelled:
              return (
                <>
                  <span className="hover-action-content">
                    <CustomTooltip title="Edit">
                      <IconButton
                        color={'primary'}
                        onClick={(e: any) => {
                          e.stopPropagation();
                          setTransaction(data);
                          handleManualEdit();
                        }}
                      >
                        <EditTwoTone twoToneColor={theme.palette.primary.main} />
                      </IconButton>
                    </CustomTooltip>
                  </span>
                  <span className="hover-action-content">
                    <CustomTooltip title="Cancel">
                      <IconButton
                        sx={{
                          color: theme.palette.warning.main
                        }}
                        onClick={(e: any) => {
                          e.stopPropagation();
                          setTransaction(data);
                          setOpenCancelConfirmation(true);
                        }}
                      >
                        <StopOutlined twoToneColor={theme.palette.warning.main} />
                      </IconButton>
                    </CustomTooltip>
                  </span>
                </>
              );
            default:
              return null;
          }
        },
        sortable: false,
        enablePivot: false,
        pivot: false,
        resizable: false,
        suppressNavigable: true,
        suppressHeaderMenuButton: true,
        aggFunc: null,
        suppressColumnsToolPanel: true,
        cellClass: 'no-border'
      }
    ],
    [useCompactView]
  );

  useLayoutEffect(() => {
    function updateSize() {
      if (gridApi) {
        gridApi!.autoSizeColumns(['actions']);
      }
    }

    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, [gridApi]);

  useEffect(() => {
    if (!gridApi) {
      return;
    }
    gridApi.setGridOption('pinnedBottomRowData', [
      {
        amount: data?.mcaFeeTransactions.reduce((accumulator, transaction) => {
          return accumulator + (clearedTransactions.includes(transaction.status) ? transaction.amount || 0 : 0);
        }, 0)
      }
    ]);
  }, [data, gridApi]);

  const onGridReady = useCallback((params: GridReadyEvent) => {
    setGridApi(params.api);
    applyGridColumnState(params.api, keyName);
  }, []);
  const onRowDataUpdated = useCallback(() => {
    if (!gridApi) return;
    applyGridColumnState(gridApi, keyName);
  }, [gridApi]);

  useAgGridOutsideClick(gridApi);
  type excelTypes = {
    id: string;
    dataType: any;
  };

  const excelStyles: excelTypes[] = [
    {
      id: 'textFormat',
      dataType: 'String'
    }
  ];

  const onFilterChanged = useCallback(({ api }: FilterChangedEvent) => {
    let amountSum = 0;
    api.forEachNodeAfterFilter((rowNode) => {
      if (rowNode.data && clearedTransactions.includes(rowNode.data.status)) amountSum += rowNode.data.amount;
    });
    api.setGridOption('pinnedBottomRowData', [{ amount: amountSum }]);
  }, []);

  const pinnedBottomRowData = useMemo<any[]>(() => {
    return [{ amount: 0 }];
  }, []);

  const canReload = mca.recurrencies && mca.recurrencies.length;

  const handleExportReport = useCallback(async () => {
    if (!gridApi) {
      return;
    }
    setBackdropOpen(true);
    const qp = {
      mcaId: mca.id
    };
    await reportApi.handleExportReport('mca-transactions', qp);
    setBackdropOpen(false);
  }, [gridApi, mca.id]);

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams): (MenuItemDef | string)[] => {
      return McaTransactionsContextMenu({
        params,
        updateTransactionType,
        mcaId: mca.id,
        isSuperAdmin,
        allowedEditableTransactionTypes
      });
    },
    [isSuperAdmin, mca.id, updateTransactionType]
  );

  const saveColumnsStateOnChange = useCallback(
    (event: ColumnResizedEvent | SortChangedEvent | ColumnMovedEvent | ColumnVisibleEvent) => {
      if (
        gridApi && event.source?.toLowerCase().includes('ui') &&
        ((event.type === 'columnResized' && event.finished) ||
          (event.type === 'columnMoved' && event.finished) ||
          event.type === 'sortChanged' ||
          event.type === 'columnVisible')
      ) {
        if (event.type === 'columnVisible') {
          const colId = event.column?.getColId();
          if (colId && event.column?.isVisible()) {
            gridApi.autoSizeColumns([colId], true);
          }
        }
        const columnsState = JSON.stringify(gridApi.getColumnState());
        localStorage.setItem(keyName + '_COLUMNS_STATE', columnsState);
      }
    },
    [gridApi]
  );

  const getRowClass = useCallback(
    (params) => {
      if (params.node.rowPinned === 'bottom') {
        return 'ag-pin-bottom-row-' + mode;
      }
    },
    [mode]
  );

  return (
    <Box display="flex" flexDirection="column">
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={backdropOpen}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <MainCard border={false} boxShadow shadow={theme.customShadows.z1} sx={{ height: '100%',  p: '8px 16px 16px', ml: -0.5, mr: 0.5 }} content={false}>
        {loading  ? ( <LinearProgress /> )
            : (
                <>
        {canManage && (
          <Stack direction={'row'} pb={1} justifyContent="flex-end" alignItems="flex-end" spacing={2}>
            <CustomTooltip title="Add Fee">
              <Button
                variant="contained"
                onClick={() => {
                  setOpenCreateDialog(true);
                  setOpenEditDialog(false);
                }}
                sx={{ fontSize: '12px', minWidth: 'auto' }}
              >
                <PlusOutlined />
              </Button>
            </CustomTooltip>
            {canReload && (
              <>
                <CustomTooltip title="Update">
                <Button variant="contained" onClick={handleRefetch} sx={{ fontSize: '12px', minWidth: 'auto' }}>
                  <ReloadOutlined />
                </Button>
                </CustomTooltip>
              </>
            )}
          </Stack>
        )}
        <div
          className={`${mode === 'light' ? 'ag-theme-material' : 'ag-theme-material-dark'} ${useCompactView ? 'compact-view' : 'expanded-view'
            }`}
          style={gridStyle}
        >
          <AgGridReact<Transaction[]>
            suppressColumnVirtualisation={true}
            onRowDataUpdated={onRowDataUpdated}
            domLayout={'autoHeight'}
            sideBar={sideBar}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            rowData={transactions}
            onGridReady={onGridReady}
            onColumnResized={saveColumnsStateOnChange}
            onSortChanged={saveColumnsStateOnChange}
            onColumnMoved={saveColumnsStateOnChange}
            onColumnVisible={saveColumnsStateOnChange}
            onFilterChanged={onFilterChanged}
            pinnedBottomRowData={pinnedBottomRowData}
            getContextMenuItems={getContextMenuItems}
            excelStyles={excelStyles}
            getRowClass={getRowClass}
          ></AgGridReact>
        </div>
                </>
            )}
      </MainCard>

      {/* edit transaction note dialog */}

      <Dialog maxWidth="sm" fullWidth onClose={handleManualEdit} open={manualEdit} sx={{ '& .MuiDialog-paper': { p: 0 } }}>
        {manualEdit && (
          <AddManualTransaction mca={mca} entity={transaction} onCancel={handleManualEdit} onSave={(transaction) => refetch()} />
        )}
      </Dialog>
      <Dialog maxWidth="sm" fullWidth onClose={handleEditNote} open={editNote} sx={{ '& .MuiDialog-paper': { p: 0 } }}>
        {transaction && (
          <EditTransactionNote mca={mca} entity={transaction} onCancel={handleEditNote} onSave={(transaction) => refetch()} />
        )}
      </Dialog>

      {/* cancel transaction confirmation */}
      {openCancelConfirmation && (
        <ActionConfirmation
          title={`Confirm transaction ${transaction?.externalId || transaction?.id} canceling`}
          open={openCancelConfirmation}
          type="error"
          handleClose={handleCancelTransaction}
        ></ActionConfirmation>
      )}

      {/* create recurrence dialog */}
      <Dialog
        maxWidth="sm"
        fullWidth
        onClose={() => {
          setOpenCreateDialog(false);
          setOpenEditDialog(false);
        }}
        open={openCreateDialog || openEditDialog}
        sx={{ '& .MuiDialog-paper': { p: 0 } }}
      >
        {mca && (
          <AddRecurrence
            handleClose={() => {
              setOpenCreateDialog(false);
              setOpenEditDialog(false);
            }}
            handleSave={(recurrency, openRecurrencyAchDetails) => {
              setOpenCreateDialog(false);
              setOpenEditDialog(false);
              setSelectedRecurrency(recurrency);
              setOpenRecurrencyAchDetails(openRecurrencyAchDetails);
            }}
            mca={mca}
            transaction={openEditDialog ? transaction : null}
            isFeeRecurrence={true}
          />
        )}
      </Dialog>

      {/* ach info dialog */}
      {openRecurrencyAchDetails && selectedRecurrency && (
        <RecurrencyAchDetailsDialog
          open={openRecurrencyAchDetails}
          recurrencyId={selectedRecurrency?.id}
          handleDone={async () => {
              setOpenRecurrencyAchDetails(false);
              await refetch();
          }}
        />
      )}
    </Box>
  );
});

export default McaFeesTransactions;
